import type { FilterOption } from "@assetwatch/ui";
import { type ListFacet, ListFilters } from "../ListFilters";

type RequestFiltersProps = {
  activeFilterCount: number;
  assetFilter: string[];
  assetOptions: FilterOption[];
  canReset?: boolean;
  onAssetChange: (value: string[]) => void;
  onClearAll: () => void;
  onPriorityChange: (value: string[]) => void;
  onRequesterChange: (value: string[]) => void;
  onStatusChange: (value: string[]) => void;
  onSubmittedFromChange: (value: string | undefined) => void;
  onSubmittedToChange: (value: string | undefined) => void;
  priorities: string[];
  requestedBy: string[];
  requesters: FilterOption[];
  showRequesters: boolean;
  statusFilter: string[];
  statuses: FilterOption[];
  submittedFrom?: string;
  submittedTo?: string;
};

const priorityOptions: FilterOption[] = [
  { label: "High", value: "HIGH" },
  { label: "Medium", value: "MEDIUM" },
  { label: "Low", value: "LOW" },
];

function fromDate(value?: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function toDateKey(value: Date | null): string | undefined {
  if (!value) return undefined;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function RequestFilters({
  activeFilterCount,
  assetFilter,
  assetOptions,
  canReset,
  onAssetChange,
  onClearAll,
  onPriorityChange,
  onRequesterChange,
  onStatusChange,
  onSubmittedFromChange,
  onSubmittedToChange,
  priorities,
  requestedBy,
  requesters,
  showRequesters,
  statusFilter,
  statuses,
  submittedFrom,
  submittedTo,
}: RequestFiltersProps) {
  const facets: ListFacet[] = [
    {
      key: "asset",
      label: "Asset",
      options: assetOptions,
      value: assetFilter,
      onChange: onAssetChange,
    },
    {
      key: "date",
      label: "Date submitted",
      from: fromDate(submittedFrom),
      to: fromDate(submittedTo),
      onFromChange: (date) => onSubmittedFromChange(toDateKey(date)),
      onToChange: (date) => onSubmittedToChange(toDateKey(date)),
    },
    {
      key: "priority",
      label: "Priority",
      options: priorityOptions,
      value: priorities,
      onChange: onPriorityChange,
    },
    {
      key: "status",
      label: "Status",
      options: statuses,
      value: statusFilter,
      onChange: onStatusChange,
    },
    ...(showRequesters
      ? [
          {
            key: "requester",
            label: "Requested by",
            options: requesters,
            value: requestedBy,
            onChange: onRequesterChange,
          },
        ]
      : []),
  ];

  return (
    <ListFilters
      activeFilterCount={activeFilterCount}
      ariaLabel="Request filters"
      canReset={canReset}
      facets={facets}
      onReset={onClearAll}
    />
  );
}
